import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { formatDisplayDateTime } from "utils/dateFormat";
import NotesModal from "components/Modals/NotesModal";
import PaymentConflictAlert from "components/Payments/PaymentConflictAlert";
import { getPaymentConflict, isSettledConflict, newerPayment } from "utils/pendingPaymentChange";
import {
  rejectPayment,
  rejectTermVerification,
  useTermNotes,
  useTermVerificationDetail,
  verifyPayment,
  verifyTermVerification,
} from "./verificationTaskApi";
import {
  rejectModulePayment,
  useModulePaymentNotes,
  verifyModulePayment,
} from "./modulePaymentVerificationApi";
import { isRefundEntry } from "../../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import { invalidateModuleFinance } from "components/ModulePayments/moduleFinanceQueries";
import {
  creditMoveTermNumbers,
  isCreditAdjustment,
} from "../../Admissions/AddAdmissions/applicationFlow/creditAdjustmentUtils";

const getApiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const formatAud = (val) => {
  const n = parseFloat(val);
  return val === "" || val == null || Number.isNaN(n) ? "—" : `AUD ${n.toLocaleString()}`;
};

const formatVerificationAmount = (payment) => {
  if (payment?.amount == null || payment?.amount === "") return "—";
  const n = Math.abs(parseFloat(payment.amount));
  if (Number.isNaN(n)) return "—";
  const formatted = n.toLocaleString();
  return isRefundEntry(payment.entryType) ? `−AUD ${formatted}` : `AUD ${formatted}`;
};

const entryNoun = (payment) =>
  isCreditAdjustment(payment)
    ? "credit move"
    : isRefundEntry(payment?.entryType)
    ? "refund"
    : "payment";
const entryNounTitle = (payment) =>
  isCreditAdjustment(payment)
    ? "Credit move"
    : isRefundEntry(payment?.entryType)
    ? "Refund"
    : "Payment";

const creditMoveAmount = (payment) => formatAud(Math.abs(parseFloat(payment?.amount)));

const termMoneyRows = (term) => [
  ["Agreed amount", formatAud(term?.agreedAmount)],
  ["Verified paid", formatAud(term?.verifiedPaidAmount)],
  ["Pending", formatAud(term?.pendingPaidAmount)],
];

const DetailGrid = ({ rows }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 divide-x divide-y divide-slate-100 border border-app-border rounded-lg overflow-hidden">
    {rows.map(([label, val]) => (
      <div key={label} className="px-3 py-2">
        <p className="text-xs text-app-text-muted">{label}</p>
        <p className="font-medium text-app-text text-sm">{val ?? "—"}</p>
      </div>
    ))}
  </div>
);

const ActionBar = ({
  canAct,
  actionable,
  busy,
  onVerify,
  rejecting,
  setRejecting,
  feedback,
  setFeedback,
  onConfirmReject,
}) => {
  if (!canAct) {
    return (
      <p className="text-xs text-app-text-muted">You do not have permission to verify or reject.</p>
    );
  }
  if (!actionable) {
    return <p className="text-xs text-app-text-muted">This item is no longer pending.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutlineIcon fontSize="small" />}
          disabled={busy}
          onClick={onVerify}
          sx={{ textTransform: "none" }}
        >
          Verify
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<CancelOutlinedIcon fontSize="small" />}
          disabled={busy}
          onClick={() => setRejecting((v) => !v)}
          sx={{ textTransform: "none" }}
        >
          Reject
        </Button>
        {busy && <CircularProgress size={16} />}
      </div>
      {rejecting && (
        <div className="flex flex-col gap-2">
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Rejection feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Explain why this is rejected..."
          />
          <div className="flex gap-2">
            <Button
              size="small"
              color="error"
              variant="contained"
              disabled={!feedback.trim() || busy}
              onClick={onConfirmReject}
              sx={{ textTransform: "none" }}
            >
              Review rejection
            </Button>
            <Button
              size="small"
              onClick={() => {
                setRejecting(false);
                setFeedback("");
              }}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const VerificationTaskItem = ({ item, clientId, canAct, onActionComplete }) => {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  // Latched the moment a verify or reject succeeds. The item's own copy still says
  // PENDING_VERIFICATION until the invalidated lists come back, so without this the buttons would
  // re-enable for the length of that refetch and a second click would hit a server that has already
  // moved the payment on — an error toast on an action that actually worked.
  const [settled, setSettled] = useState(false);
  // Set when the server refused a decision because the payment changed under us: the sender edited
  // or withdrew it, or another verifier decided it first.
  const [conflict, setConflict] = useState(null);
  const payment = newerPayment(item.payment, conflict);

  const isModulePayment = item.kind === "modulePayment";
  const isPayment = item.kind === "payment" || isModulePayment;
  const isCreditMove = item.kind === "payment" && isCreditAdjustment(payment);
  const isRefund = isPayment && !isCreditMove && isRefundEntry(payment?.entryType);
  const moveTerms = creditMoveTermNumbers(item);
  const moveLabel = `T${moveTerms.from ?? "?"} to T${moveTerms.to ?? "?"}`;

  const termDetail = useTermVerificationDetail(
    item.admissionId,
    item.termIndex,
    expanded && !isPayment
  );
  // Admission terms have their own subtable so they get discussion-backed term notes; other
  // modules (Visa, ...) have no subtable, so they read/write the linked entry's own AccountNote list.
  const termNotes = useTermNotes(
    clientId,
    item.admissionId,
    item.termIndex,
    expanded && !isModulePayment
  );
  const moduleNotes = useModulePaymentNotes(
    item.clientAccountDetailId,
    expanded && isModulePayment
  );
  const notes = isModulePayment ? moduleNotes : termNotes;

  /**
   * Returned to the mutation rather than fired and forgotten: react-query keeps the mutation in its
   * loading state until the promise an onSuccess returns resolves, so awaiting the refetches here is
   * what holds `busy` — and the disabled buttons — across the whole gap between the toast and the
   * list actually catching up.
   */
  const refreshLists = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admissionTerms"] }),
      queryClient.invalidateQueries({ queryKey: ["pending-payment-verifications", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["pending-term-verifications", clientId] }),
      queryClient.invalidateQueries({
        queryKey: ["pending-module-payment-verifications", clientId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["account-verification-by-term", item.admissionId, item.termIndex],
      }),
      isModulePayment
        ? invalidateModuleFinance(queryClient, item.moduleType, item.moduleId, clientId)
        : Promise.resolve(),
    ]);

  const handleDone = async (message) => {
    setSettled(true);
    setConflict(null);
    toast.success(message);
    setRejecting(false);
    setRejectDialogOpen(false);
    setFeedback("");
    await refreshLists();
    onActionComplete?.();
  };

  /**
   * A 409 means the payment is not what this card showed. An edit keeps the card and swaps in the new
   * figures so the verifier reviews them before deciding; an approval, rejection or withdrawal by
   * someone else ends the card, and the refreshed list drops it.
   */
  const handleActionError = async (err, fallback) => {
    const found = getPaymentConflict(err);
    if (!found) {
      toast.error(getApiError(err, fallback));
      return;
    }
    setConflict(found);
    setVerifyDialogOpen(false);
    setRejectDialogOpen(false);
    if (isSettledConflict(found)) {
      setSettled(true);
      setRejecting(false);
      toast.info(found.message);
    } else {
      toast.warning(found.message);
    }
    await refreshLists();
    if (isSettledConflict(found)) onActionComplete?.();
  };

  const verifyMutation = useMutation({
    mutationFn: () => {
      if (isModulePayment) return verifyModulePayment(payment.id, payment.revision);
      return isPayment
        ? verifyPayment(payment.id, payment.revision)
        : verifyTermVerification(termDetail.data?.id);
    },
    onSuccess: () =>
      handleDone(
        isPayment
          ? isCreditMove
            ? "Credit move verified on both terms"
            : isRefund
            ? "Refund verified"
            : "Payment verified"
          : "Verified and imported into accounts"
      ),
    onError: (err) => handleActionError(err, "Failed to verify"),
  });

  const rejectMutation = useMutation({
    mutationFn: () => {
      if (isModulePayment) return rejectModulePayment(payment.id, feedback, payment.revision);
      return isPayment
        ? rejectPayment(payment.id, feedback, payment.revision)
        : rejectTermVerification(termDetail.data?.id, feedback);
    },
    onSuccess: () =>
      handleDone(
        isPayment
          ? isCreditMove
            ? "Credit move rejected"
            : isRefund
            ? "Refund verification rejected"
            : "Payment verification rejected"
          : "Rejected"
      ),
    onError: (err) => handleActionError(err, "Failed to reject"),
  });

  const busy = verifyMutation.isLoading || rejectMutation.isLoading;
  const loadingDetail = !isPayment && termDetail.isLoading;
  const termNotesCount = notes.data?.length ?? 0;

  const actionable =
    !settled &&
    (isPayment
      ? payment?.status === "PENDING_VERIFICATION"
      : !!termDetail.data?.id && termDetail.data?.status === "PENDING");

  const renderTermDetail = () => {
    const termRow = termDetail.data?.submittedAccountDetail?.admissionDetails?.[0];
    if (!termDetail.data) {
      return (
        <p className="text-sm text-app-text-muted">No verification record found for this term.</p>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {termRow && (
          <DetailGrid
            rows={[
              ["Term Fee", termRow.perSemFee],
              ["Material Fee", termRow.materialFee],
              ["Discount", termRow.discount],
              ["Agreed Amount", termRow.agreed ?? termRow.agreedAmount],
              ["Paid Amount", termRow.paidAmount],
              ["Due", termRow.dueAmount],
            ]}
          />
        )}
      </div>
    );
  };

  const renderCreditMoveDetail = () => {
    const out = item.creditAdjustment?.out;
    const into = item.creditAdjustment?.in;
    return (
      <div className="flex flex-col gap-3">
        <div className="border border-app-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-app-border bg-sky-50 text-sky-900 text-sm">
            This is a <strong>credit move</strong>: {creditMoveAmount(payment)} of what was overpaid
            on T{moveTerms.from ?? "?"} is used to pay T{moveTerms.to ?? "?"}. Verifying or
            rejecting acts on both terms together.
          </div>
          <DetailGrid
            rows={[
              ["From", `T${moveTerms.from ?? "?"}`],
              ["To", `T${moveTerms.to ?? "?"}`],
              ["Amount", creditMoveAmount(payment)],
              ["Requested on", payment?.date],
              ["Requested by", payment?.submittedByName],
            ]}
          />
          {payment?.notes && (
            <div className="px-3 py-2 border-t border-app-border">
              <p className="text-xs text-app-text-muted">Reason</p>
              <p className="text-sm text-app-text whitespace-pre-wrap">{payment.notes}</p>
            </div>
          )}
        </div>
        {out?.term && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold text-app-text-muted">
              T{moveTerms.from} (giving credit)
            </p>
            <DetailGrid rows={termMoneyRows(out.term)} />
          </div>
        )}
        {into?.term && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold text-app-text-muted">
              T{moveTerms.to} (taking credit)
            </p>
            <DetailGrid
              rows={[...termMoneyRows(into.term), ["Due", formatAud(into.term.dueAmount)]]}
            />
          </div>
        )}
      </div>
    );
  };

  const renderPaymentDetail = () => {
    if (isCreditMove) return renderCreditMoveDetail();
    const term = item.term;
    return (
      <div className="flex flex-col gap-3">
        {term && (
          <DetailGrid
            rows={[
              ["Admission date", term.admissionDate],
              ["Census date", term.censusDate],
              ["Term fee", formatAud(term.perSemFee)],
              ["Material fee", formatAud(term.materialFee)],
              ["Discount", formatAud(term.discount)],
              ["Agreed amount", formatAud(term.agreedAmount)],
              ["Verified paid", formatAud(term.verifiedPaidAmount)],
              ["Pending", formatAud(term.pendingPaidAmount)],
              ["Due", formatAud(term.dueAmount)],
            ]}
          />
        )}
        {payment && (
          <div className="border border-app-border rounded-lg overflow-hidden">
            {isRefund && (
              <div className="px-3 py-2 border-b border-app-border bg-amber-50 text-amber-900 text-sm">
                This request is a <strong>refund</strong>. Verifying will reduce the term&apos;s
                verified paid amount.
              </div>
            )}
            <DetailGrid
              rows={[
                ["Type", entryNounTitle(payment)],
                ["Amount", formatVerificationAmount(payment)],
                ["Date", payment.date],
                ["Reference", payment.reference || "—"],
              ]}
            />
            {payment.notes && (
              <div className="px-3 py-2 border-t border-app-border">
                <p className="text-xs text-app-text-muted">Notes</p>
                <p className="text-sm text-app-text whitespace-pre-wrap">{payment.notes}</p>
              </div>
            )}
            {payment.attachments?.length > 0 && (
              <div className="px-3 py-2 border-t border-app-border">
                <p className="text-xs text-app-text-muted mb-1">Attachments</p>
                <PaymentReceiptChips urls={payment.attachments} sx={{}} />
              </div>
            )}
          </div>
        )}
        {isPayment && payment?.auditLogs?.length > 0 && (
          <Accordion
            disableGutters
            defaultExpanded={false}
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
              <span className="text-xs text-app-text-muted">
                History ({payment.auditLogs.length})
              </span>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {payment.auditLogs.map((l, i) => (
                <div
                  key={i}
                  className="px-3 py-1.5 text-xs text-app-text-secondary border-b border-slate-50 last:border-0"
                >
                  <span className="font-medium">{l.action}</span>
                  {l.actorName ? ` — ${l.actorName}` : ""}
                  {l.timestamp ? ` · ${formatDisplayDateTime(l.timestamp)}` : ""}
                  {l.details ? <div className="text-app-text-muted">{l.details}</div> : null}
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </div>
    );
  };

  const summaryLabel = isPayment
    ? item.label ||
      (payment?.amount
        ? `${entryNounTitle(payment)} — ${formatVerificationAmount(payment)}`
        : `${entryNounTitle(payment)} verification`)
    : item.label || "Term verification";

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, v) => setExpanded(v)}
        disableGutters
        sx={{ border: "1px solid #e2e8f0", borderRadius: 1, "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-app-text">{summaryLabel}</span>
            {isRefund && (
              <Chip label="Refund" color="warning" size="small" sx={{ height: 18, fontSize: 10 }} />
            )}
            {isCreditMove && (
              <Chip
                label="Credit move"
                color="info"
                size="small"
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
            <Chip label="Pending" color="warning" size="small" sx={{ height: 18, fontSize: 10 }} />
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="flex flex-col gap-3">
            <div className="flex justify-end">
              <Tooltip
                title={termNotesCount ? `${termNotesCount} note(s)` : "No notes yet — click to add"}
                arrow
              >
                <Badge badgeContent={termNotesCount} color="error" max={99}>
                  <Button
                    variant={termNotesCount > 0 ? "contained" : "outlined"}
                    color="primary"
                    size="small"
                    startIcon={<StickyNote2Outlined />}
                    onClick={() => setNotesOpen(true)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Notes
                  </Button>
                </Badge>
              </Tooltip>
            </div>
            {loadingDetail ? (
              <div className="flex items-center gap-2 text-sm text-app-text-muted">
                <CircularProgress size={16} /> Loading details…
              </div>
            ) : isPayment ? (
              renderPaymentDetail()
            ) : (
              renderTermDetail()
            )}
            {isPayment && <PaymentConflictAlert conflict={conflict} />}
            {isPayment &&
              !conflict &&
              payment?.status === "PENDING_VERIFICATION" &&
              payment?.lastEditedByName && (
                <p className="text-xs text-app-text-muted">
                  Edited by {payment.lastEditedByName}
                  {payment.lastEditedAt ? ` · ${formatDisplayDateTime(payment.lastEditedAt)}` : ""}
                </p>
              )}
            {!loadingDetail && (
              <ActionBar
                canAct={canAct}
                actionable={actionable}
                busy={busy}
                onVerify={() => setVerifyDialogOpen(true)}
                rejecting={rejecting}
                setRejecting={setRejecting}
                feedback={feedback}
                setFeedback={setFeedback}
                onConfirmReject={() => setRejectDialogOpen(true)}
              />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !busy && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm rejection?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isCreditMove
              ? `Reject moving ${creditMoveAmount(
                  payment
                )} from ${moveLabel}? Both terms stay as they are, and the requester can update the reason and resubmit.`
              : isPayment
              ? `Reject this ${entryNoun(payment)}${
                  payment?.amount ? ` of ${formatVerificationAmount(payment)}` : ""
                } for ${
                  item.term?.termLabel ?? item.label ?? "this record"
                }? The submitter will need to edit and resubmit.`
              : "Reject this term verification? The submitter will need to correct and resubmit."}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Rejection feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Explain why this is rejected..."
            helperText={`This message is shown to the person who submitted the ${entryNoun(
              payment
            )} for verification.`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!feedback.trim() || busy}
            onClick={() => rejectMutation.mutate()}
          >
            {busy ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={verifyDialogOpen}
        onClose={() => !busy && setVerifyDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm verification?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isCreditMove
              ? `Verify moving ${creditMoveAmount(
                  payment
                )} from ${moveLabel}? The first term's paid amount drops and the second's rises by the same amount.`
              : isPayment
              ? `Verify this ${entryNoun(payment)}${
                  payment?.amount ? ` of ${formatVerificationAmount(payment)}` : ""
                } for ${item.term?.termLabel ?? item.label ?? "this record"}?${
                  isRefund
                    ? " The term's verified paid amount will be reduced."
                    : " It will be marked as verified in accounts."
                }`
              : "Verify and import this term into accounts? This cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifyDialogOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            disabled={busy}
            onClick={() => {
              verifyMutation.mutate(undefined, {
                onSettled: () => setVerifyDialogOpen(false),
              });
            }}
          >
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>
      {notesOpen && (
        <NotesModal
          open={notesOpen}
          handleClose={() => {
            setNotesOpen(false);
            notes.refetch?.();
          }}
          studentId={clientId}
          type={
            isModulePayment ? undefined : `admission_term_${item.admissionId}_${item.termIndex}`
          }
          parentId={isModulePayment ? item.clientAccountDetailId : undefined}
          parentType={isModulePayment ? "ACCOUNT_DETAIL" : undefined}
          attachmentsEnabled={true}
        />
      )}
    </>
  );
};

export default VerificationTaskItem;
